import { track } from '@cartwheel/analytics'
import { Announcement, AnnouncementType, Card, Tooltip } from '@cartwheel/components'

export type ContractUtilizationCardProps = {
  utilized: number
  ready_for_assignment: number
  in_outreach: number
  remaining: number
  total: number
  tier_minimum: number
  tier_maximum: number
  program_manager_email: string
  user_name?: string
  user_roles?: string[]
  district_name?: string
  district_id?: string
}

const clampPercent = (value: number) => Math.max(0, Math.min(value, 100))

export default function ContractUtilizationCard({
  utilized,
  ready_for_assignment: readyForAssignment,
  in_outreach: inOutreach,
  remaining,
  total,
  tier_minimum: tierMinimum,
  tier_maximum: tierMaximum,
  program_manager_email: programManagerEmail,
  user_name: userName,
  user_roles: userRoles,
  district_name: districtName,
  district_id: districtId,
}: ContractUtilizationCardProps) {
  const safeTotal = total > 0 ? total : 0
  const segmentPercent = (value: number) => (safeTotal > 0 ? (value / safeTotal) * 100 : 0)

  const utilizedPercent = clampPercent(segmentPercent(utilized))
  const availablePercent = Math.max(0, 100 - utilizedPercent)
  const rawPipelinePercent = segmentPercent(readyForAssignment) + segmentPercent(inOutreach)
  const pipelineExceedsAvailable = rawPipelinePercent > availablePercent
  const hasPipeline = rawPipelinePercent > 0
  const pipelineScale =
    pipelineExceedsAvailable && hasPipeline ? availablePercent / rawPipelinePercent : 1
  const readyPercent = clampPercent(segmentPercent(readyForAssignment) * pipelineScale)
  const outreachPercent = clampPercent(segmentPercent(inOutreach) * pipelineScale)
  const safeRemaining = Math.max(remaining, 0)
  const remainingPercent = safeTotal > 0 ? (safeRemaining / safeTotal) * 100 : 0
  const showCongratulations = safeTotal > 0 && remainingPercent <= 5
  const isFullyUtilized = safeTotal > 0 && safeRemaining === 0

  const isTierAligned = tierMaximum === total && tierMinimum !== tierMaximum
  const tierMinimumPercent = isTierAligned && safeTotal > 0 ? (tierMinimum / safeTotal) * 100 : 0

  const onClickCSM = () => {
    track('Click Email CSM', {
      userName: userName,
      userRoles: userRoles,
      district: districtName,
      districtId: districtId,
      programManagerEmail: programManagerEmail,
      reason: isFullyUtilized ? 'contract_usage_full' : 'contract_usage_approaching_utilization',
    })
  }

  return (
    <Card.Root variant="outline" className="gap-10 p-6 h-full">
      <Card.Header>
        <div className="flex w-full items-start justify-between gap-2">
          <div className="flex items-start gap-2">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white-dark">
              <i className="fa-solid fa-file text-[20px] text-forest-light" aria-hidden="true" />
            </div>
            <div className="flex flex-col">
              <div className="text-xl font-semibold text-charcoal">Contract Usage</div>
              <div className="text-sm font-normal text-charcoal-50">
                {isTierAligned
                  ? `Your tier has ${tierMinimum}-${tierMaximum} referrals.`
                  : `Your contract has ${total} referrals.`}
              </div>
            </div>
          </div>
        </div>
      </Card.Header>

      <Card.Content className="flex flex-col gap-2">
        <div className="relative">
          <div className="relative h-[18px] w-full overflow-hidden rounded-[12px] bg-charcoal-25/20">
            <div
              data-testid="bar-utilized"
              className="absolute inset-y-0 left-0 h-full bg-forest-light"
              style={{ width: `${utilizedPercent}%` }}
            />
            <div
              data-testid="bar-ready"
              className="absolute inset-y-0 bg-mint"
              style={{
                left: `${utilizedPercent}%`,
                width: `${readyPercent}%`,
              }}
            />
            <div
              data-testid="bar-outreach"
              className="absolute inset-y-0 bg-sea"
              style={{
                left: `${utilizedPercent + readyPercent}%`,
                width: `${outreachPercent}%`,
              }}
            />
            {/* Tier minimum marker */}
            {tierMinimumPercent > 0 && tierMinimumPercent <= 100 && (
              <div
                className="absolute inset-y-0 w-1 bg-charcoal-50"
                style={{ left: `${tierMinimumPercent}%` }}
              />
            )}
          </div>
        </div>
        <div className="relative flex items-center justify-end text-sm text-charcoal-75">
          {/* Tier minimum label - aligned with the fractional display */}
          {tierMinimumPercent > 0 && tierMinimumPercent <= 100 && (
            <div
              className="absolute text-sm text-charcoal-75"
              style={{
                left: `${tierMinimumPercent}%`,
                transform: 'translateX(-50%)',
              }}
            >
              {tierMinimum}
            </div>
          )}
          <div>{total}</div>
        </div>
      </Card.Content>

      <div className="flex flex-wrap gap-6 text-sm text-charcoal-75">
        <div className="flex items-center gap-2">
          <span className="h-[18px] w-[18px] rounded-full bg-forest-light" aria-hidden="true" />
          {utilized} utilized
        </div>
        <div className="flex items-center gap-2">
          <span className="h-[18px] w-[18px] rounded-full bg-mint" aria-hidden="true" />
          {readyForAssignment} in Ready for Assignment
        </div>
        <div className="flex items-center gap-2">
          <span className="h-[18px] w-[18px] rounded-full bg-sea" aria-hidden="true" />
          {inOutreach} in Outreach
        </div>
        <Tooltip text="Contract max - utilized">
          <div className="flex items-center gap-2">
            <span className="h-[18px] w-[18px] rounded-full bg-charcoal-25/20" aria-hidden="true" />
            {safeRemaining} remaining
          </div>
        </Tooltip>
      </div>
      {showCongratulations ? (
        <div className="-mt-3">
          <Announcement
            type={AnnouncementType.Success}
            title={<span className="text-base leading-6">Congratulations!</span>}
            message={
              <p className="text-base leading-6">
                {isFullyUtilized
                  ? "You've utilized your full contract, enabling your students to get the mental health care they deserve. "
                  : "You've almost utilized your full contract, enabling your students to get the mental health care they deserve. "}
                <a
                  className="underline"
                  href={`mailto:${programManagerEmail}`}
                  onClick={onClickCSM}
                >
                  Contact your CSM for next steps
                </a>
                .
              </p>
            }
            icon="fa-party-horn"
          />
        </div>
      ) : null}
    </Card.Root>
  )
}
